import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import ButtonStyles, { SecondaryButtonStyles, } from '@stories/Components/Buttons/Button/Button.styles';
import DropdownFilterStyles from '@stories/Components/Listings/DropdownFilter/DropdownFilter.styles';
import FormStyles from '@stories/Components/Misc/Forms/Form.styles';
import SelectStyles from '@stories/Components/Misc/Forms/Inputs/Select/Select.styles';
import SearchboxStyles from '@stories/Components/Misc/Searchbox/Searchbox.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
const Container = styled.section `
  ${baseGrid};

  --applyHeight: 82px;
  --itemHeight: ${fluid(40, 48)};

  > ${ButtonStyles.Container} {
    --borderRadius: 12px;
    --buttonHeight: var(--itemHeight);
    --fontSize: ${fluid(14, 18)};

    justify-content: space-between;
    grid-column: span 6;

    span[data-desktop] {
      display: none;
    }

    @media ${from(Device.DesktopSmall)} {
      ${SecondaryButtonStyles};

      grid-column: span 3;
      margin-bottom: auto;

      span[data-mobile] {
        display: none;
      }

      span[data-desktop] {
        display: block;
      }
    }
  }

  &[data-show-filter-btn='false'] {
    @media ${from(Device.DesktopSmall)} {
      > ${ButtonStyles.Container} {
        visibility: hidden;
        pointer-events: none;
      }
    }
  }

  @media ${from(Device.DesktopSmall)} {
    grid-template-columns: repeat(24, 1fr);
  }

  ${FormStyles.Field} {
    grid-column: span 6;

    @media ${from(Device.DesktopSmall)} {
      grid-column: span 3;
      margin-bottom: auto;
    }
  }

  ${SelectStyles.Select}, ${SearchboxStyles.Form} {
    height: var(--itemHeight);
    border-color: ${brand.primary.purple.light};
    border-radius: 12px;
  }

  ${SelectStyles.Select}, ${SearchboxStyles.Input} {
    font-size: ${fluid(14, 18)};
  }

  ${SearchboxStyles.Button} {
    order: 1;
  }
`;
const FiltersWrapper = styled.div `
  ${transition('opacity, transform', 0.5)};

  display: flex;
  gap: 22px;

  @media ${until(Device.DesktopSmall)} {
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    width: 100%;
    max-width: 767px;
    height: calc(100vh - var(--applyHeight));
    padding: 12px var(--sitePadding, 16px) 16px;
    background-color: ${brand.global.white};
    z-index: ${Layers.Navigation + 1};

    &[data-expanded='false'] {
      opacity: 0;
      transform: translateX(100%);
      pointer-events: none;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: span 18;
    gap: 16px var(--gutterWidth);
    flex-wrap: wrap;

    ${SearchboxStyles.Container},
    ${DropdownFilterStyles.Container} {
      flex: 0 0 calc(33% - (var(--gutterWidth) * 0.54));
    }
  }
`;
const ExtraWrapper = styled(m.div) `
  flex: 0 0 100%;
  display: flex;
  gap: 16px var(--gutterWidth);
  flex-wrap: wrap;

  @media ${until(Device.DesktopSmall)} {
    display: contents;

    ${SearchboxStyles.Container} {
      order: -1;
    }
  }
`;
const SelectedWrapper = styled.div `
  flex: 0 0 100%;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const Top = styled.div `
  ${fonts.lato.bold};

  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 18px;
  color: ${brand.primary.purple.light};
  padding-bottom: 14px;
  border-bottom: 1px solid ${brand.grey.mid};
  order: -1;

  > svg {
    display: block;
    width: 31px;
  }

  ${ButtonStyles.Container} {
    --buttonHeight: 36px;

    margin-left: auto;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const SelectedOption = styled(m.button) `
  ${ButtonReset};
  ${transition('background-color')};
  ${fonts.lato.regular};

  background-color: rgba(167, 239, 246, 0.4);
  display: flex;
  gap: 22px;
  align-items: center;
  border-radius: 40px;
  color: ${brand.global.black};
  font-size: 18px;
  line-height: 28px;
  padding: 12px ${fluid(12, 18)};

  svg {
    width: 10px;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
  }

  &:focus-visible,
  &:hover {
    background-color: rgba(167, 239, 246, 0.6);
  }

  &[data-all] {
    background-color: transparent;

    &:focus-visible,
    &:hover {
      background-color: rgba(167, 239, 246, 0.2);
    }
  }
`;
const Bottom = styled.div `
  ${transition('opacity, transform', 0.5)};

  display: flex;
  align-items: center;
  width: 100%;
  height: var(--applyHeight);
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  max-width: 767px;
  padding: 0 var(--sitePadding, 16px);
  background-color: ${brand.global.white};
  z-index: ${Layers.Navigation + 3};
  box-shadow: 6px 6px 12.6px 7px rgba(0, 0, 0, 0.1);

  ${Container}:has(${FiltersWrapper}[data-expanded='false']) & {
    opacity: 0;
    transform: translateX(100%);
    pointer-events: none;
  }

  ${ButtonStyles.Container} {
    --buttonHeight: 48px;

    width: 100%;
    justify-content: center;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const FiltersStyles = {
    Container,
    FiltersWrapper,
    Top,
    Bottom,
    ExtraWrapper,
    SelectedWrapper,
    SelectedOption,
};
export default FiltersStyles;
